import React from 'react';
import Layout from '../components/layout/Layout';
import type { AuthState } from '../types';
import SEOHead from '../components/SEOHead';

interface PrivacyProps {
  authState: AuthState;
  setAuthState: (authState: AuthState) => void;
}

const Privacy: React.FC<PrivacyProps> = ({ authState, setAuthState }) => {
  return (
    <Layout authState={authState} setAuthState={setAuthState}>
      <SEOHead
        title="Privacy Policy"
        description="Read our Privacy Policy to understand how we collect, use, and protect your information."
        canonicalUrl="/privacy"
      />
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last updated:</strong> January 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                We collect information you provide directly to us when you:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Create an account using Google OAuth</li>
                <li>Add your portfolio holdings</li>
                <li>Configure notification endpoints</li>
                <li>Set up alerts and trading rules</li>
                <li>Contact us for support</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Types of information we collect:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Account Information:</strong> Name, email address, Google account ID</li>
                <li><strong>Portfolio Data:</strong> Stock symbols, quantities, purchase prices, broker account details</li>
                <li><strong>Notification Settings:</strong> Email addresses and phone numbers for alerts</li>
                <li><strong>Usage Data:</strong> How you interact with our service, features used, alert triggers</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide and maintain the MarketEye service</li>
                <li>Process your portfolio data and generate alerts</li>
                <li>Send notifications when your alert conditions are met</li>
                <li>Improve our service and develop new features</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our platform (email delivery, data storage, analytics)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>All data is encrypted in transit using SSL/TLS</li>
                <li>Sensitive data is encrypted at rest</li>
                <li>Broker credentials are encrypted before storage</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication requirements</li>
                <li>Secure data centers with physical security</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as your account is active or as needed to provide services. You can request deletion of your data at any time by contacting us.
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Account Data:</strong> Retained while your account is active</li>
                <li><strong>Portfolio Data:</strong> Retained until you delete your account or specific holdings</li>
                <li><strong>Alert History:</strong> Retained for 2 years for audit purposes</li>
                <li><strong>Usage Logs:</strong> Retained for 1 year for service improvement</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
              <p className="text-gray-700 mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Objection:</strong> Object to certain processing of your data</li>
                <li><strong>Withdrawal:</strong> Withdraw consent for data processing</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Maintain your session and authentication</li>
                <li>Remember your preferences and settings</li>
                <li>Analyze how you use our service</li>
                <li>Improve our website performance</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                Our service integrates with third-party services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Google OAuth:</strong> For secure authentication</li>
                <li><strong>Email Providers:</strong> For sending notifications (SendGrid, etc.)</li>
                <li><strong>WhatsApp Business API:</strong> For WhatsApp notifications</li>
                <li><strong>Market Data Providers:</strong> For real-time stock prices</li>
              </ul>
              <p className="text-gray-700 mb-4">
                These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                MarketEye is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If you believe we have collected information from a child under 18, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this privacy policy from time to time. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Posting the updated policy on our website</li>
                <li>Sending an email notification to your registered email address</li>
                <li>Displaying a notice in the MarketEye application</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this privacy policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong>Email:</strong> <a href="mailto:ankitdaf@gmail.com" className="text-blue-600 hover:text-blue-700">ankitdaf@gmail.com</a>
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Address:</strong> MarketEye Privacy Team<br />
                  BOM / BLR , IN
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy; 